import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { IconButton, Label } from 'shared/ui'
import { useShortcutTooltip } from 'shared/ui/ShortcutTooltip/useShortcutTooltip'
import { useChipsClick } from 'shared/hooks/useChipsClick.ts'
import { PromptFieldText } from 'entities/Chatbot/utils/constants'
import {
  EnhancePrompt,
  type IEnhancePromptProps,
} from 'entities/AIAgent/actions/enhancePrompt/EnhancePrompt'
import { AiCallingApi } from 'entities/AICalling/api/aiCallingApi'
import { sendEnhancePromptLogEvent } from 'entities/AIAgent/actions/enhancePrompt/sendEnhancePromptLogEvent'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type IAICallingType } from 'entities/AICalling/api/types'
import {
  addContactNoteTemplate,
  addConversationNoteTemplate,
  addTagTemplate,
  handoffToHumanTemplate,
  removeTagTemplate,
  sendSMSTemplate,
} from 'entities/TemplateActions/actions.tsx'
import { MessageField } from 'widgets/MessageField'
import { ShortcutContent } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/ShortcutContent/ShortcutContent.tsx'
import { onHandoffToHumanChipClick } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/handofToHuman/onHandoffToHumanChipClick.ts'
import { onAddConversationNoteChipClick } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/addConversationNote/onAddConversationNoteChipClick.ts'
import { onAddContactNoteChipClick } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/addContactNote/onAddContactNoteChipClick.ts'
import { onSendSMSChipClick } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/sendSMS/onSendSMSChipClick.ts'
import { onAddTagChipClick } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/addTag/onAddTagChipClick.ts'
import { onRemoveTagChipClick } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/removeTag/onRemoveTagChipClick.ts'
import { type AgentInstructionsStore } from './store/AgentInstructionsStore'

const agentTypeMap: Record<IAICallingType, string> = {
  faq_information: 'FAQ',
  custom: 'custom',
  take_message: 'take a message',
}

export const AgentInstructions = observer(({ store }: { store: AgentInstructionsStore }) => {
  const {
    init,
    error,
    setError,
    instructions,
    setInstruction,
    resetInstructions,
    parent,
    messageFieldStore,
  } = store
  const entities = useEntitiesContext()
  const { textFieldElement, replaceTriggerContent, updateTriggerContent } = messageFieldStore

  const { triggerTooltip, infos } = useShortcutTooltip({
    textFieldElement,
    actions: [
      {
        shortcut: '/',
        infoLabel: 'to add contact fields and actions',
        renderComponent: (onClose) => <ShortcutContent store={store} onClose={onClose} />,
      },
    ],
  })

  useChipsClick({
    textFieldElement,
    replaceTriggerContent,
    updateTriggerContent,
    actions: {
      [addTagTemplate.regex]: onAddTagChipClick(store),
      [removeTagTemplate.regex]: onRemoveTagChipClick(store),
      [sendSMSTemplate.regex]: onSendSMSChipClick(store),
      [addContactNoteTemplate.regex]: onAddContactNoteChipClick(store),
      [addConversationNoteTemplate.regex]: onAddConversationNoteChipClick(store),
      [handoffToHumanTemplate.regex]: onHandoffToHumanChipClick(store),
    },
  })

  useEffect(init, [])

  const requestEnhance: IEnhancePromptProps['requestEnhance'] = async (prompt, signal) => {
    if (!parent.params) return ''
    const template_type = parent.params?.template_type

    sendEnhancePromptLogEvent(entities, {
      agent_id: parent.response?.id,
      agentType: template_type ? `calling agent - ${agentTypeMap[template_type]}` : '',
    })

    const res = await AiCallingApi.enhancePrompt(
      {
        instructions: prompt,
        template_type,
        knowledge_base_enabled: parent.params.knowledge_base_enabled,
        knowledge_base_all_documents: parent.params.knowledge_base_all_documents,
        knowledge_base_documents: parent.params.knowledge_base_documents,
      },
      { signal }
    )
    return res.data.improved_prompt
  }

  return (
    <div>
      {triggerTooltip}
      <Label
        marginBottom={4}
        questionTooltipProps={{
          label: PromptFieldText.instructions.tooltipLabel,
          breakWord: true,
        }}
        label={{
          text: 'Agent instructions',
          rightNode: (
            <>
              <EnhancePrompt
                prompt={instructions}
                onAcceptPrompt={setInstruction}
                setError={setError}
                requestEnhance={requestEnhance}
              />
              <IconButton
                iconProps={{ fontSize: 16 }}
                variant={'icon'}
                color={'tertiary'}
                size={'small'}
                icon={'back'}
                ariaLabel='Reset'
                onClick={resetInstructions}
                tooltipProps={{
                  label: 'Reset to default',
                  placement: 'top',
                }}
              />
            </>
          ),
        }}
      />
      <MessageField
        messageFieldStore={messageFieldStore}
        uiStyle={'new'}
        error={error}
        additionalBottomContent={infos}
      />
    </div>
  )
})
